/**
 * DynamicColumnPicker — popover for toggling visible columns.
 * Only columns with displayConfig.listVisible (or showInList) are offered.
 */
import { Columns3 } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Checkbox } from '../ui/checkbox';
import type { DynamicField } from '../../types/dynamicEntity';

interface DynamicColumnPickerProps {
  columns: DynamicField[];
  visibleColumnKeys: string[];
  onVisibleColumnsChange: (keys: string[]) => void;
}

export function DynamicColumnPicker({
  columns,
  visibleColumnKeys,
  onVisibleColumnsChange,
}: DynamicColumnPickerProps) {
  const toggleColumn = (key: string, checked: boolean) => {
    if (checked) {
      onVisibleColumnsChange([...visibleColumnKeys, key]);
    } else {
      // keep at least one column visible
      const next = visibleColumnKeys.filter((k) => k !== key);
      if (next.length > 0) onVisibleColumnsChange(next);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 text-[11px] gap-1.5">
          <Columns3 className="w-3 h-3" />
          Columns
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="end">
        <p className="text-[11px] font-semibold text-foreground mb-2 px-1">Toggle Columns</p>
        <div className="space-y-0.5 max-h-72 overflow-y-auto">
          {columns.map((col) => {
            const isVisible = visibleColumnKeys.includes(col.internalName);
            return (
              <div
                key={col.fieldId}
                className="flex items-center gap-2 px-1 py-1 rounded hover:bg-muted cursor-pointer"
                onClick={() => toggleColumn(col.internalName, !isVisible)}
              >
                <Checkbox
                  id={`col-pick-${col.fieldId}`}
                  checked={isVisible}
                  onCheckedChange={(c) => toggleColumn(col.internalName, !!c)}
                  onClick={(e) => e.stopPropagation()}
                />
                <label
                  htmlFor={`col-pick-${col.fieldId}`}
                  className="text-[11px] cursor-pointer select-none flex-1"
                >
                  {col.label}
                </label>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-border px-1">
          <button
            type="button"
            className="text-[11px] text-muted-foreground hover:text-foreground"
            onClick={() => onVisibleColumnsChange(columns.map((c) => c.internalName))}
          >
            Show all
          </button>
          <button
            type="button"
            className="text-[11px] text-muted-foreground hover:text-foreground"
            onClick={() => {
              const first = columns[0]?.internalName;
              if (first) onVisibleColumnsChange([first]);
            }}
          >
            Reset
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
